/**
 * Headless: keeps the demo store's server snapshot fresh for the planning horizon
 * (demo day −7 … +28 days). Mounted once by HomeHealthDemoRoot while the layer is on.
 */
import { useEffect, useMemo, useRef } from "react";
import { useHomeHealthDemoHorizonQuery } from "~/graphql/__generated__/graphql";
import { useOrgConfig } from "~/providers/OrgConfigProvider";
import { isTenantSeeded, resolveBindings } from "../engine/bindings";
import { addDaysToKey, dateKeyRange, dateKeyStart } from "@caredna-marlin/utils/homeHealthDemo/engine/clock";
import { tenantSeed } from "../engine/tenantDemoDay";
import { followTenantSeed, getDemoStore } from "../store/instance";
import { useDemoDispatch, useDemoSelector } from "../store/useDemoStore";
import type { DateKey } from "@caredna-marlin/utils/homeHealthDemo/state";
import { normalizeHorizon, snapshotFingerprint } from "./normalize";

/**
 * Fourteen days back, not seven: continuity (4.1) and planned-versus-actual (5.3)
 * read completed visits behind demo day, and a weekly patient's second-to-last
 * visit is 14 days back. Do not shrink it without re-reading both.
 */
export const HORIZON_DAYS_BEFORE = 14;
/**
 * Thirteen weeks, matching `SHIFT_HORIZON_AFTER` in the seed and the routine
 * visit types' `daysBeforeLastOption`. It used to be 28, which is why the roster
 * grid drew a greyed "standing pattern, not yet published" beyond four weeks —
 * a state the product does not have, since its nightly worker generates shifts
 * out to the scheduling limit. The three numbers move together or the invented
 * state comes back.
 *
 * The cost is small despite the range tripling: the query's one `$start`/`$end`
 * pair also bounds visits and blocks, but the seed places **no** visits past +28,
 * so what actually arrives is the extra shifts this exists to fetch. Widening it
 * by adding a second range would change the document, and a changed document
 * needs codegen and a service redeploy for the persisted-operation allowlist.
 */
export const HORIZON_DAYS_AFTER = 91;
export const HORIZON_POLL_MS = 30_000;

export function horizonRange(demoToday: DateKey, timeZone: string) {
  const rangeStart = addDaysToKey(demoToday, -HORIZON_DAYS_BEFORE);
  const rangeEnd = addDaysToKey(demoToday, HORIZON_DAYS_AFTER);
  return {
    rangeStart,
    rangeEnd,
    startIso: dateKeyStart(rangeStart, timeZone).toISOString(),
    endIso: dateKeyStart(addDaysToKey(rangeEnd, 1), timeZone).toISOString(),
  };
}

export function HorizonFetcher() {
  const dispatch = useDemoDispatch();
  const orgConfig = useOrgConfig();
  const demoToday = useDemoSelector((s) => s.meta.demoToday);
  const timeZone = useDemoSelector((s) => s.config.branch.timeZone);
  const range = useMemo(() => horizonRange(demoToday, timeZone), [demoToday, timeZone]);
  const lastFingerprint = useRef<string>();
  const lastBindings = useRef<string>();

  const { data } = useHomeHealthDemoHorizonQuery({
    variables: { start: range.startIso, end: range.endIso },
    fetchPolicy: "cache-and-network",
    pollInterval: HORIZON_POLL_MS,
    context: { queryDeduplication: false },
  });

  useEffect(() => {
    if (!data) return;
    /**
     * **A reseeded tenant rebuilds the store.** The Demo day marker is written
     * new by every reseed, so its stamp differs from the one this store was
     * built for exactly when the tenant under it has changed — however many
     * times a day that happens. No marker says nothing (a seed still being
     * applied, or one from before the marker), and the store keeps what it has.
     *
     * The store is rebuilt clean and both dedupe keys are dropped, so the
     * snapshot and the bindings below are dispatched again for the new tenant.
     * When the reseed moved the demo day, this data was fetched for the old
     * day's range; the new range refetches, and that result is the one used.
     */
    const seed = tenantSeed(data.service);
    if (followTenantSeed(seed)) {
      lastFingerprint.current = undefined;
      lastBindings.current = undefined;
      if (seed?.day !== demoToday) return;
    }
    const snapshot = normalizeHorizon(data, new Date().toISOString(), range.rangeStart, range.rangeEnd);
    const fingerprint = snapshotFingerprint(snapshot);
    if (fingerprint !== lastFingerprint.current) {
      lastFingerprint.current = fingerprint;
      dispatch({ type: "SERVER_SNAPSHOT", snapshot });
    }
    // Bind fixture keys to the tenant's ids whenever the catalog or the snapshot changes.
    const catalog = {
      visitTypes: Object.values(orgConfig.visitTypes).filter(Boolean).map((vt) => ({ id: vt!.id, name: vt!.name })),
      shiftCenters: Object.values(orgConfig.shiftCenters).filter(Boolean).map((sc) => ({ id: sc!.id, name: sc!.name })),
      serviceLines: Object.values(orgConfig.serviceLines).filter(Boolean).map((sl) => ({ id: sl!.id, name: sl!.name })),
    };
    const bindings = resolveBindings(getDemoStore().getState(), snapshot, catalog);
    const seeded = isTenantSeeded(bindings, snapshot);
    const realDays = seeded ? dateKeyRange(range.rangeStart, range.rangeEnd) : [];
    const key = JSON.stringify([bindings, realDays.length]);
    if (key !== lastBindings.current) {
      lastBindings.current = key;
      dispatch({ type: "TENANT_BOUND", bindings, realDays });
    }
  }, [data, dispatch, range, demoToday, orgConfig]);

  return null;
}
